import type React from 'react';
import { useCloudflareImage } from '@/hooks/useCloudflareImage';
import { useOptimizedBreakpoints } from '@/components/ResponsiveImages/hooks/useImageUrls';

interface UseResponsiveImagePreloadReturn {
  mobileImageUrl: string;
  tabletImageUrl: string;
  desktopImageUrl: string;
  preloadLinks: React.ReactElement[];
}

// Avatar-specific configuration to match AvatarImage component
const AVATAR_IMAGE_CONFIG = {
  quality: {
    placeholder: 1,
    mobile: 70,
    tablet: 75,
    desktop: 80,
  },
  placeholderBlur: 10,
} as const;

const createAvatarBreakpoints = (
  mobileSize: number,
  tabletSize: number,
  desktopSize: number
) => [
  // Mobile (1x)
  {
    width: mobileSize,
    height: mobileSize,
    quality: AVATAR_IMAGE_CONFIG.quality.mobile,
  },
  // Tablet (1.5x for high-DPI)
  {
    width: tabletSize * 1.5,
    height: tabletSize * 1.5,
    quality: AVATAR_IMAGE_CONFIG.quality.tablet,
  },
  // Desktop (2x for high-DPI)
  {
    width: desktopSize * 2,
    height: desktopSize * 2,
    quality: AVATAR_IMAGE_CONFIG.quality.desktop,
  },
];

export const useResponsiveImagePreload = (
  src: string,
  variant: 'blurred' | 'normal' | 'content' = 'blurred'
): UseResponsiveImagePreloadReturn => {
  // For blurred backgrounds, we only need the first 3 breakpoints (mobile, tablet, desktop)
  const [mobileBreakpoint, tabletBreakpoint, desktopBreakpoint] =
    useOptimizedBreakpoints(variant);

  const mobileImageUrl = useCloudflareImage({
    src: src,
    ...mobileBreakpoint,
  });

  const tabletImageUrl = useCloudflareImage({
    src: src,
    ...tabletBreakpoint,
  });

  const desktopImageUrl = useCloudflareImage({
    src: src,
    ...desktopBreakpoint,
  });

  const preloadLinks = src
    ? [
        // Mobile preload
        <link
          key="mobile"
          rel="preload"
          as="image"
          href={mobileImageUrl}
          media="(max-width: 767px)"
        />,
        // Tablet preload
        <link
          key="tablet"
          rel="preload"
          as="image"
          href={tabletImageUrl}
          media="(min-width: 768px) and (max-width: 1023px)"
        />,
        // Desktop preload
        <link
          key="desktop"
          rel="preload"
          as="image"
          href={desktopImageUrl}
          media="(min-width: 1024px)"
        />,
      ]
    : [];

  return {
    mobileImageUrl,
    tabletImageUrl,
    desktopImageUrl,
    preloadLinks,
  };
};

// Specialized hook for avatar images that matches AvatarImage component
export const useAvatarImagePreload = (
  src: string,
  mobileSize: number = 32,
  tabletSize: number = 32,
  desktopSize: number = 32
): { preloadLinks: React.ReactElement[] } => {
  const [mobileBreakpoint, tabletBreakpoint, desktopBreakpoint] =
    createAvatarBreakpoints(mobileSize, tabletSize, desktopSize);

  const mobileImageUrl = useCloudflareImage({
    src,
    ...mobileBreakpoint,
  });

  const tabletImageUrl = useCloudflareImage({
    src,
    ...tabletBreakpoint,
  });

  const desktopImageUrl = useCloudflareImage({
    src,
    ...desktopBreakpoint,
  });

  const preloadLinks = src
    ? [
        // Mobile preload
        <link
          key="avatar-mobile"
          rel="preload"
          as="image"
          href={mobileImageUrl}
          media="(max-width: 767px)"
        />,
        // Tablet preload
        <link
          key="avatar-tablet"
          rel="preload"
          as="image"
          href={tabletImageUrl}
          media="(min-width: 768px) and (max-width: 1023px)"
        />,
        // Desktop preload
        <link
          key="avatar-desktop"
          rel="preload"
          as="image"
          href={desktopImageUrl}
          media="(min-width: 1024px)"
        />,
      ]
    : [];

  return { preloadLinks };
};
